import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Box,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBroom } from "@fortawesome/free-solid-svg-icons";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import ModalDialogTitle from "./ModalDialogTitle";

// Firestore batched writes cap at 500 operations — stay under that.
const BATCH_SIZE = 450;

// Mirrors the new-student validation added to EditUpload.js: a row with no
// First/Last name isn't a real student record — it's junk (a stray
// spreadsheet summary row, a bulk-upload row that lost most of its fields,
// etc.) that slipped into TpStudents before that validation existed.
const isInvalidStudentDoc = (data) => {
  const hasFirst = data.FirstName && String(data.FirstName).trim();
  const hasLast = data.LastName && String(data.LastName).trim();
  return !hasFirst && !hasLast;
};

const describeDoc = (docSnap) => {
  const data = docSnap.data();
  const knownFields = Object.keys(data).filter(
    (k) => !["created_at", "updatedAt"].includes(k),
  );
  const preview = knownFields
    .slice(0, 4)
    .map((k) => `${k}: ${data[k]}`)
    .join(", ");
  return preview || "(no identifying fields)";
};

/**
 * Scans the whole TpStudents collection for documents missing both
 * FirstName and LastName — not real students, just malformed records from
 * incomplete bulk uploads — previews them, and deletes only those on
 * confirmation. Firestore can't query "field is missing" server-side, so
 * this reads the full collection client-side to find them.
 *
 * @param {Function} [onCleared] - Called once deletion finishes successfully.
 * @param {boolean} [disabled] - e.g. tie to the page's canEdit permission.
 */
const RemoveInvalidStudentsButton = ({ onCleared, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [invalidDocs, setInvalidDocs] = useState(null);
  const [progress, setProgress] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleOpen = async () => {
    setOpen(true);
    setScanning(true);
    setDeleting(false);
    setInvalidDocs(null);
    setProgress(0);
    setDeletedCount(0);
    setDone(false);
    setError("");

    try {
      const snap = await getDocs(collection(db, "TpStudents"));
      const invalid = snap.docs.filter((d) => isInvalidStudentDoc(d.data()));
      setInvalidDocs(invalid);
    } catch (err) {
      console.error("Error scanning TpStudents for invalid records:", err);
      setError("Something went wrong while scanning. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const handleClose = () => {
    if (deleting) return; // Don't allow closing mid-delete.
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (!invalidDocs || invalidDocs.length === 0) return;
    setDeleting(true);
    setError("");
    try {
      let deleted = 0;
      for (let i = 0; i < invalidDocs.length; i += BATCH_SIZE) {
        const chunk = invalidDocs.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((d) => batch.delete(doc(db, "TpStudents", d.id)));
        await batch.commit();
        deleted += chunk.length;
        setDeletedCount(deleted);
        setProgress(Math.round((deleted / invalidDocs.length) * 100));
      }
      setDone(true);
      onCleared?.();
    } catch (err) {
      console.error("Error deleting invalid TpStudents records:", err);
      setError(
        "Something went wrong while deleting these records. Please try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<FontAwesomeIcon icon={faBroom} />}
      >
        Remove Invalid Records
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Remove Invalid Student Records
        </ModalDialogTitle>
        <DialogContent>
          {scanning && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography>Scanning TpStudents for invalid records…</Typography>
              <LinearProgress />
            </Box>
          )}

          {!scanning && !done && invalidDocs && (
            <>
              {invalidDocs.length === 0 ? (
                <Typography>
                  No invalid records found — every document has a First Name
                  or Last Name.
                </Typography>
              ) : (
                <>
                  <Typography sx={{ mb: 1 }}>
                    Found {invalidDocs.length} document
                    {invalidDocs.length === 1 ? "" : "s"} with no First Name
                    or Last Name. These aren&apos;t real student records —
                    review the preview below, then confirm to delete them.
                  </Typography>
                  <List
                    dense
                    sx={{
                      maxHeight: 240,
                      overflowY: "auto",
                      border: "1px solid #eee",
                      borderRadius: 1,
                    }}
                  >
                    {invalidDocs.slice(0, 50).map((d) => (
                      <ListItem key={d.id} divider>
                        <ListItemText
                          primary={d.id}
                          secondary={describeDoc(d)}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {invalidDocs.length > 50 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Showing first 50 of {invalidDocs.length}.
                    </Typography>
                  )}
                </>
              )}
            </>
          )}

          {deleting && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Deleting {deletedCount} of {invalidDocs?.length}…
              </Typography>
            </Box>
          )}

          {done && (
            <Typography>
              Done — {deletedCount} invalid record
              {deletedCount === 1 ? "" : "s"} deleted.
            </Typography>
          )}

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        {!done && !scanning && invalidDocs && invalidDocs.length > 0 && (
          <DialogActions>
            <Button
              onClick={handleConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting
                ? "Deleting…"
                : `Delete ${invalidDocs.length} Record${
                    invalidDocs.length === 1 ? "" : "s"
                  }`}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default RemoveInvalidStudentsButton;
